/**
 * Postcode keyboard derivation.
 *
 * The POSTAL_CODE keyboard is derived from the resolved validation regex — the
 * station-locale pattern from `postcodeRegexByLocale.js` (see
 * Question.getValidationConfiguration). We classify that regex:
 *   - numeric-only postcode  -> numeric keypad (with an auto-inserted separator
 *     where the regex defines one, e.g. Poland `\d{2}-\d{3}`)
 *   - postcode allowing letters, or no regex at all -> the standard alphabetic
 *     keyboard (the universal superset that can enter any postcode)
 *
 * The locale regexes are authored cleanly (real `\d` / `\s`), so this module takes
 * the pattern as-is — no runtime repair.
 */

/**
 * Does the regex permit an alphabetic character anywhere? Digit and whitespace
 * classes are removed first so only *real* letter requirements (literal letters,
 * `[A-Z]`, `\w`) remain.
 *
 * This only ever runs against the clean per-locale regexes (the keyboard derives from the
 * station-locale pattern via Question.getPostcodeKeyboardRegex; a survey's own config regex
 * always gets the full alphanumeric keyboard, so it never reaches here).
 *
 * @param {string} pattern
 * @returns {boolean}
 */
function requiresLetters(pattern) {
    if (/\\w/.test(pattern)) return true;
    const withoutDigitsAndSpace = pattern.replace(/\\d/g, "").replace(/\\s/g, "");
    return /[A-Za-z]/.test(withoutDigitsAndSpace);
}

/**
 * Build a linear digit/separator mask from a numeric regex, or null when the
 * pattern has no separator, is an alternation (ambiguous), or can't be parsed.
 *
 * @param {string} pattern
 * @returns {Array<object>|null}
 */
function buildMask(pattern) {
    if (!pattern || pattern.includes("|")) return null;

    let body = pattern;
    if (body.startsWith("^")) body = body.slice(1);
    if (body.endsWith("$")) body = body.slice(0, -1);

    const mask = [];
    let hasSeparator = false;
    let i = 0;

    const match = re => {
        re.lastIndex = i;
        const m = re.exec(body);
        return m ? m : null;
    };

    while (i < body.length) {
        let m;
        if ((m = match(/\\d\{(\d+)\}/y))) {
            mask.push({ type: "digits", count: parseInt(m[1], 10) });
        } else if ((m = match(/\\d/y))) {
            mask.push({ type: "digits", count: 1 });
        } else if ((m = match(/\\s(\?)?/y))) {
            mask.push({ type: "separator", char: " ", optional: Boolean(m[1]) });
            hasSeparator = true;
        } else if ((m = match(/-/y))) {
            mask.push({ type: "separator", char: "-", optional: false });
            hasSeparator = true;
        } else if ((m = match(/\[ \](\{0,1\}|\?)?/y))) {
            mask.push({ type: "separator", char: " ", optional: Boolean(m[1]) });
            hasSeparator = true;
        } else {
            // Unrecognised token (e.g. `[0-9]` groups, wrapping parens) — we can't
            // build a reliable mask, so fall back to plain numeric input.
            return null;
        }
        i += m[0].length;
    }

    return hasSeparator ? mask : null;
}

/**
 * Classify a POSTAL_CODE regex into a keyboard profile.
 *
 * @param {string} pattern resolved station-locale regex pattern (see Question.getRegexPattern)
 * @returns {{ keyboard: "numpad"|"standard", mask: Array<object>|null }}
 */
export function analyzePostcodeRegex(pattern) {
    if (!pattern || !pattern.trim()) {
        return { keyboard: "standard", mask: null };
    }

    if (requiresLetters(pattern)) {
        return { keyboard: "standard", mask: null };
    }

    return { keyboard: "numpad", mask: buildMask(pattern) };
}

/**
 * Reformat a numeric postcode value against a mask, auto-inserting separators.
 * Strips non-digits first, so it is idempotent and handles backspace cleanly.
 *
 * @param {string} value
 * @param {Array<object>|null} mask
 * @returns {string}
 */
export function applyPostcodeMask(value, mask) {
    if (!mask) return value;

    const totalDigits = mask.reduce((sum, seg) => (seg.type === "digits" ? sum + seg.count : sum), 0);
    const digits = String(value)
        .replace(/\D/g, "")
        .slice(0, totalDigits);

    let out = "";
    let idx = 0;
    for (const seg of mask) {
        if (seg.type === "digits") {
            const part = digits.slice(idx, idx + seg.count);
            out += part;
            idx += part.length;
        } else if (idx < digits.length) {
            // Only emit the separator once there are further digits to show after it
            out += seg.char;
        }
    }

    return out;
}

// Per-locale canonical-space normalisers for postcodes entered on the standard (alphanumeric)
// keyboard. Each takes the COMPACT, uppercased value (whitespace stripped) and returns the
// canonically spaced form, or null if it doesn't match the expected shape (then the original
// is kept and validation handles it). The space anchor differs per country — see comments.
const POSTCODE_VALUE_NORMALISERS = {
    // GB: outward (2-4) + space + inward (always 3: digit + 2 letters) -> anchored to the END.
    en_GB: compact => {
        const match = compact.match(/^([A-Z]{1,2}[0-9][A-Z0-9]?)([0-9][A-Z]{2})$/);
        return match ? `${match[1]} ${match[2]}` : null;
    },
    // IE Eircode: routing key (3) + space + unique identifier (4) -> anchored to the START.
    en_IE: compact => (/^[A-Z0-9]{7}$/.test(compact) ? `${compact.slice(0, 3)} ${compact.slice(3)}` : null),
    // NL: 4 digits + space + 2 letters -> anchored to the START.
    nl_NL: compact => {
        const match = compact.match(/^([0-9]{4})([A-Z]{2})$/);
        return match ? `${match[1]} ${match[2]}` : null;
    }
};

/**
 * Normalises a postcode value to its canonical, single-spaced form for the locales that need it
 * (GB, IE, NL). Applied on submit so the stored value is canonical regardless of how the user
 * spaced/cased it (e.g. "sw1a1aa" -> "SW1A 1AA"). Locales without a normaliser, or values that
 * don't match the expected shape, are returned unchanged (validation then handles them).
 *
 * @param {string} value
 * @param {string|null|undefined} languageTag - the station locale (e.g. "en_GB" / "en-GB")
 * @returns {string}
 */
export function normalizePostcodeValue(value, languageTag) {
    if (!value || !languageTag) {
        return value;
    }
    const normaliser = POSTCODE_VALUE_NORMALISERS[languageTag.replace("-", "_")];
    if (!normaliser) {
        return value;
    }
    const compact = value.replace(/\s+/g, "").toUpperCase();
    return normaliser(compact) || value;
}
